/**
 * Where the emailed verification link lands: /verify-email?token=...
 *
 * Verifies on mount rather than behind a button. The user already expressed
 * intent by clicking the link in their inbox; asking them to click again is
 * friction for no gain.
 *
 * Guarded against React's development double-invoke of effects: the token is
 * single-use, so firing twice would show a spurious "already used" error on a
 * link that worked perfectly.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError, verifyEmail } from '../lib/api.js';
import { c, primaryButton } from '../theme.js';
import { AuthLayout, ErrorBox, NoticeBox } from './AuthLayout.js';

type Status = 'verifying' | 'done' | 'failed';

interface Props {
  token: string;
  /** Clears the token from the URL and continues into the app. */
  onContinue: () => void;
}

export function VerifyEmailScreen({ token, onContinue }: Props) {
  const [status, setStatus] = useState<Status>('verifying');
  const [error, setError] = useState('');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    verifyEmail(token)
      .then(() => setStatus('done'))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'We couldn’t confirm your email address.');
        setStatus('failed');
      });
  }, [token]);

  if (status === 'verifying') {
    return (
      <AuthLayout heading="Confirming your email…">
        <p style={{ fontSize: 13.5, color: c.muted, margin: 0 }}>One moment.</p>
      </AuthLayout>
    );
  }

  if (status === 'failed') {
    return (
      <AuthLayout heading="That link didn’t work">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <ErrorBox>{error}</ErrorBox>
          <p style={{ fontSize: 13, color: c.muted, margin: 0, lineHeight: 1.6 }}>
            Verification links last 24 hours and work once. You can send yourself a fresh one from
            inside the app.
          </p>
          <button type="button" onClick={onContinue} style={primaryButton}>
            Continue
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout heading="Email confirmed">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <NoticeBox>
          Thanks. We can now reach you with expiry alerts, low-stock warnings and password resets.
        </NoticeBox>
        <button type="button" onClick={onContinue} style={primaryButton}>
          Continue
        </button>
      </div>
    </AuthLayout>
  );
}
